import React, { useState, useEffect, useCallback } from 'react';
import { Drawer } from 'antd';
import DatePicker from 'react-datepicker';
import NumberFormat from 'react-number-format';
import Moment from 'moment';
import { toast } from '@rickylandino/react-messages';
import ReactLoading from 'react-loading';
import Axios from '../../config/axios';
import Globals from '../../config/globals';
import statesList from '../Helpers/StatesList';
import { getPatientInformationByIDNew } from '../../common/services/ApiService';

const states = statesList
    .filter(obj => obj.country_code === 'US' || obj.country_code === 'CA')
    .sort((a, b) => a.name.localeCompare(b.name));

const labelStyle = { display: 'block', fontSize: '0.8rem', fontWeight: 500, marginBottom: 4 };
const inputStyle = (theme) => ({
    width: '100%',
    padding: '6px 10px',
    borderRadius: 6,
    border: `1px solid ${theme === 'dark' ? '#52525b' : '#d4d4d8'}`,
    backgroundColor: theme === 'dark' ? '#18181b' : '#fff',
    color: theme === 'dark' ? '#e4e4e7' : '#18181b',
});

function PatientDemographicsSlideout({ patientId, visible, onClose, onSaved }) {
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [patientModel, setPatientModel] = useState(null);
    const [ethnicBackground, setEthnicBackground] = useState([]);
    const [formFields, setFormFields] = useState(null);

    const theme = Globals.theme;

    useEffect(() => {
        if (!visible || !patientId) return;

        setLoading(true);
        getPatientInformationByIDNew(patientId).then((data) => {
            if (!data?.patientInformation?.patientModel) {
                toast.error('Failed to load patient information.');
                setLoading(false);
                return;
            }
            setPatientModel(data.patientInformation.patientModel);
            setFormFields(data.patientInformation.patientModel);
            setEthnicBackground(data.patientEthnicity || []);
            setLoading(false);
        }).catch(() => {
            toast.error('Failed to load patient information.');
            setLoading(false);
        });
    }, [visible, patientId]);

    const handleInputChange = useCallback((event) => {
        const target = event.target;
        const value = target.type === 'checkbox' ? target.checked : target.value;
        const name = target.name;
        setFormFields((prev) => ({ ...prev, [name]: value }));
    }, []);

    const handleDateChange = useCallback((date) => {
        setFormFields((prev) => ({ ...prev, dob: date }));
    }, []);

    const doSave = useCallback((printAfterSave) => {
        if (!formFields) return;

        if (!formFields.lName || !formFields.fName || !formFields.mrn || !formFields.dob ||
            !formFields.address1 || !formFields.city || !formFields.state || !formFields.zip) {
            toast.error('Please fill in all required data');
            return;
        }

        const dobDate = new Date(formFields.dob);
        const today = new Date();
        dobDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        if (dobDate >= today) {
            toast.error('Date of birth cannot be today or in the future.');
            return;
        }

        const userInfo = JSON.parse(window.sessionStorage.getItem('userInfo'));

        setSaving(true);

        const postdata = {
            patient: formFields,
            ethnicBackground,
            userID: userInfo.userId,
        };

        Axios.post('/api/UpdatePatient', postdata).then((response) => {
            setSaving(false);
            if (response.data) {
                toast.success('Patient has been updated');
                onSaved && onSaved(printAfterSave);
                onClose();
            } else {
                toast.error('MRN is not unique. Please try with a unique MRN.');
            }
        }).catch(() => {
            setSaving(false);
            toast.error('Something went wrong');
        });
    }, [formFields, ethnicBackground, onSaved, onClose]);

    return (
        <Drawer
            title="Edit Demographics"
            placement="right"
            width={480}
            visible={visible}
            onClose={onClose}
            destroyOnClose
        >
            {loading || !formFields ? (
                <div className="d-flex justify-content-center py-5">
                    <ReactLoading type="spokes" color="#F30079" height={30} width={30} />
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <div style={{ color: theme === 'dark' ? '#a1a1aa' : '#71717a', fontSize: '0.85rem' }}>
                        {patientModel?.lName}, {patientModel?.fName} — {patientModel?.mrn}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Last Name *</label>
                            <input type="text" name="lName" value={formFields.lName || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                        </div>
                        <div>
                            <label style={labelStyle}>First Name *</label>
                            <input type="text" name="fName" value={formFields.fName || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                        </div>
                        <div>
                            <label style={labelStyle}>Middle Name</label>
                            <input type="text" name="mName" value={formFields.mName || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                        </div>
                        <div>
                            <label style={labelStyle}>DOB *</label>
                            <DatePicker
                                selected={formFields.dob ? Moment(formFields.dob).utc().toDate() : null}
                                onChange={handleDateChange}
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode="select"
                                className="form-control-custom"
                                wrapperClassName="w-100"
                            />
                        </div>
                    </div>

                    <div>
                        <label style={labelStyle}>Address 1 *</label>
                        <input type="text" name="address1" value={formFields.address1 || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                    </div>
                    <div>
                        <label style={labelStyle}>Address 2</label>
                        <input type="text" name="address2" value={formFields.address2 || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>City *</label>
                            <input type="text" name="city" value={formFields.city || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                        </div>
                        <div>
                            <label style={labelStyle}>State *</label>
                            <select name="state" value={formFields.state || ''} onChange={handleInputChange} style={inputStyle(theme)}>
                                <option value="" />
                                {states.map((state, idx) => <option key={idx} value={state.state_code}>{state.name}</option>)}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Zip *</label>
                            <input type="text" name="zip" value={formFields.zip || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                        <div>
                            <label style={labelStyle}>Cell Phone</label>
                            <NumberFormat
                                name="cellPhone"
                                value={formFields.cellPhone || ''}
                                onChange={handleInputChange}
                                format="(###) ###-####"
                                mask="_"
                                style={inputStyle(theme)}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Home Phone</label>
                            <NumberFormat
                                name="homePhone"
                                value={formFields.homePhone || ''}
                                onChange={handleInputChange}
                                format="(###) ###-####"
                                mask="_"
                                style={inputStyle(theme)}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Work Phone</label>
                            <NumberFormat
                                name="workPhone"
                                value={formFields.workPhone || ''}
                                onChange={handleInputChange}
                                format="(###) ###-####"
                                mask="_"
                                style={inputStyle(theme)}
                            />
                        </div>
                        <div>
                            <label style={labelStyle}>Ext.</label>
                            <input type="text" name="workPhoneExt" value={formFields.workPhoneExt || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                        </div>
                    </div>

                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" name="consentToTexts" checked={!!formFields.consentToTexts} onChange={handleInputChange} />
                        Patient consents to receive text alerts to Cell Phone
                    </label>

                    <div>
                        <label style={labelStyle}>Email</label>
                        <input type="text" name="email" value={formFields.email || ''} onChange={handleInputChange} style={inputStyle(theme)} />
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.85rem', cursor: 'pointer' }}>
                        <input type="checkbox" name="consentToEmails" checked={!!formFields.consentToEmails} onChange={handleInputChange} />
                        Patient consents to receive emails
                    </label>

                    {saving ? (
                        <div className="py-2">
                            <ReactLoading type="spokes" color="#F30079" height={30} width={30} />
                        </div>
                    ) : (
                        <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                            <button className="btn btn-submit" onClick={() => doSave(false)}>Save</button>
                            <button className="btn btn-outline-default" onClick={() => doSave(true)}>Save & Print</button>
                        </div>
                    )}
                </div>
            )}
        </Drawer>
    );
}

export default PatientDemographicsSlideout;
